// frontend/src/components/admin/OwnerConsumptionDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';

const OwnerConsumptionDashboard = () => {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchOwnerDashboard();
    const interval = setInterval(fetchOwnerDashboard, 30000);
    return () => clearInterval(interval);
  }, [ownerId]);

  const fetchOwnerDashboard = async () => {
    try {
      const response = await api.get(`/monitoring/owner/${ownerId}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return Math.round(amount).toLocaleString('fr-FR');
  };

  if (loading) return <Loading />;

  if (!data) {
    return (
      <div className="p-6">
        <Card>
          <p className="text-center text-gray-500 py-8">
            Aucune donnée disponible
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <button
            onClick={() => navigate('/owners')}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            ← Retour aux Owners
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            Consommation de {data.owner.name}
          </h1>
          <p className="text-gray-600 mt-1">{data.owner.email}</p>
        </div>
      </div>

      {/* Totaux */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Associations actives</h3>
          <p className="text-4xl font-bold">{data.count}</p>
        </Card>

        {/* ✅ CORRIGÉ : énergie du mois, pas valeur brute */}
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Énergie ce mois</h3>
          <p className="text-4xl font-bold">{data.totalConsumption.energy.toFixed(3)}</p>
          <p className="text-sm mt-1">kWh (cumulé depuis le 1er)</p>
        </Card>

        {/* ✅ CORRIGÉ : coût TTC du mois */}
        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <h3 className="text-lg font-semibold mb-2">Coût total ce mois</h3>
          <p className="text-4xl font-bold">{formatCurrency(data.totalConsumption.cost)}</p>
          <p className="text-sm mt-1">FCFA TTC</p>
        </Card>
      </div>

      {/* Liste des associations */}
      <Card title="📊 Détail par Association">
        {data.associations.length > 0 ? (
          <div className="space-y-4">
            {data.associations.map((item) => (
              <div
                key={item.association.id}
                className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-gray-800">{item.association.name}</h4>
                    <div className="flex space-x-2 mt-1">
                      {item.association.active && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Actif
                        </span>
                      )}
                      {item.association.autoMode && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          Auto
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/associations/${item.association.id}/dashboard`)}
                    className="text-xs"
                  >
                    📊 Voir détails
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* Puissance → temps réel, OK */}
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">Puissance</p>
                    <p className="text-lg font-bold text-blue-600">
                      {item.consumption.power.toFixed(1)} W
                    </p>
                    <p className="text-xs text-gray-400">temps réel</p>
                  </div>

                  {/* ✅ CORRIGÉ : costs.consumption.kwh = différence du mois */}
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">Énergie ce mois</p>
                    <p className="text-lg font-bold text-green-600">
                      {item.costs.consumption.kwh.toFixed(3)} kWh
                    </p>
                    <p className="text-xs text-gray-400">depuis le 1er</p>
                  </div>

                  {/* Coût HT */}
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">Coût (HT)</p>
                    <p className="text-lg font-bold text-orange-600">
                      {formatCurrency(item.costs.totals.costWithoutTaxes)} FCFA
                    </p>
                  </div>

                  {/* Coût TTC */}
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">Coût (TTC)</p>
                    <p className="text-lg font-bold text-red-600">
                      {formatCurrency(item.costs.totals.totalCost)} FCFA
                    </p>
                  </div>
                </div>

                {/* ✅ NOUVEAU : Barre de progression consommation vs mois précédent */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>
                      📊 {item.costs.consumption.kwh.toFixed(3)} kWh 
                      × {item.costs.consumption.pricePerKwh || 81.03} FCFA/kWh
                    </span>
                    <span>
                      TVA: {formatCurrency(item.costs.totals.totalTVA)} FCFA
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            Aucune association pour ce Owner
          </p>
        )}
      </Card>
    </div>
  );
};

export default OwnerConsumptionDashboard;